import {
  memo,
  forwardRef,
  useCallback,
  useState,
  useMemo,
  useEffect,
} from "react"
import { classes, belowViewPort } from "utils"
import { colors, typography } from "design"
import { Layout } from "./layout.js"
import { TextButton } from "./text-button.js"
import { useLocation, useNavigate, useSearchParams } from "react-router-dom"

export const tableVariablesLight = {
  "--resbird-table-background-color": colors.white,
  "--resbird-table-border-color": colors.grey300,
  "--resbird-table-header-row-background": colors.primaryBlueGrey,
  "--resbird-table-header-cell-color": colors.white,
}

const tableStyle = {
  minWidth: "100%",
  borderSpacing: 0,
  tableLayout: "fixed",
  borderRadius: 6,
  background: "var(--resbird-table-background-color)",
  td: {
    fontSize: "0.875rem",
    ...belowViewPort(1441, { fontSize: "0.75rem" }),
  },
}

const tableContainerStyleDefault = {
  // height: 500, // Fixed height for the table container
  minHeight: 500,
  overflow: "auto",
}

const rowStyle = {
  background: "var(--resbird-table-header-row-background)",
}

const PagerButton = memo(({ className, page, active, ...props }) => (
  <button
    className={classes("pager-button", className)}
    type="button"
    css={[
      {
        height: 32,
        width: 32,
        cursor: "pointer",
        background: colors.white,
        borderRadius: 4,
        border: `1px solid ${colors.primaryBlueGrey}`,
        "&:not(:disabled):hover": {
          fontWeight: "800",
        },
      },
      active && {
        borderColor: colors.linkBlue,
        color: colors.primaryBlueGrey,
        fontWeight: 800,
      },
    ]}
    {...props}
  >
    {page}
  </button>
))

PagerButton.displayName = "PagerButton"

export const EmptyTableRow = ({ colSpan, isPending, fetchError, data }) =>
  !isPending &&
  _.isEmpty(data) && (
    <tr className="empty-row">
      <td
        colSpan={colSpan}
        className="empty-table-cell"
        css={{ textAlign: "left" }}
      >
        {fetchError || t("widgets.table.no_data")}
      </td>
    </tr>
  )

const Footer = memo(({ perPage = 10, total, onChangePage }) => {
  const [searchParams] = useSearchParams()
  const [currentPage, setCurrentPage] = useState(1)
  const location = useLocation()
  const navigate = useNavigate()

  const end = useMemo(
    () => (currentPage - 1) * perPage + 10,
    [currentPage, perPage],
  )

  const numberOfPages = useMemo(
    () => Math.ceil(total / perPage),
    [perPage, total],
  )

  const changeSearchParams = useCallback(
    (page) => {
      if (onChangePage) onChangePage({ page: page - 1, size: perPage })
      else
        navigate(
          {
            pathname: location.pathname,
            search: `?page=${page - 1}&size=${perPage}`,
          },
          {state:location.state}
        )
    },
    [location, navigate, onChangePage, perPage],
  )

  const navigateToPage = useCallback(
    (direction) => {
      let page
      if (direction === "prev") page = currentPage - 1
      else page = currentPage + 1
      setCurrentPage(page)
      changeSearchParams(page)
    },
    [changeSearchParams, currentPage],
  )

  const onPageClick = useCallback(
    (page) => {
      setCurrentPage(page)
      changeSearchParams(page)
    },
    [changeSearchParams],
  )

  useEffect(() => {
    // If onChangePage is present, that means the search params cannot be used
    // for page logic as the table will be rendered within a main route. eg: manage offices
    if(onChangePage)
      return
    const updatedParams = new URLSearchParams(searchParams)
    if (+updatedParams.get("page") !== currentPage - 1) setCurrentPage(1)
  }, [currentPage, onChangePage, searchParams])

  const pagerButtons = useMemo(() => {
    const pages = []

    if (numberOfPages <= 10) {
      // If number of items are <= 10, show all the buttons
      for (let i = 1; i <= numberOfPages; i++) {
        pages.push(
          <PagerButton
            key={i}
            page={i}
            className={`page-${i}`}
            active={currentPage === i}
            onClick={() => onPageClick(i)}
          >
            {i}
          </PagerButton>,
        )
      }
    } else {
      // If number of items are > 10
      // Show first 3 pages and the last page, with "..." in between if necessary
      if (currentPage <= 4) {
        for (let i = 1; i <= 4; i++) {
          pages.push(
            <PagerButton
              key={i}
              page={i}
              className={`page-${i}`}
              active={currentPage === i}
              onClick={() => onPageClick(i)}
            >
              {i}
            </PagerButton>,
          )
        }
        pages.push(<div key="ellipsis1">...</div>)
        pages.push(
          <PagerButton
            key={numberOfPages}
            page={numberOfPages}
            className={`page-${numberOfPages}`}
            active={currentPage === numberOfPages}
            onClick={() => onPageClick(numberOfPages)}
          >
            {numberOfPages}
          </PagerButton>,
        )
      } else if (currentPage > numberOfPages - 4) {
        pages.push(
          <PagerButton
            key={1}
            page={1}
            className="page-1"
            active={currentPage === 1}
            onClick={() => onPageClick(1)}
          >
            1
          </PagerButton>,
        )
        pages.push(<div key="ellipsis1">...</div>)
        for (let i = numberOfPages - 3; i <= numberOfPages; i++) {
          pages.push(
            <PagerButton
              key={i}
              page={i}
              className={`page-${i}`}
              active={currentPage === i}
              onClick={() => onPageClick(i)}
            >
              {i}
            </PagerButton>,
          )
        }
      } else {
        pages.push(
          <PagerButton
            key={1}
            page={1}
            className="page-1"
            active={currentPage === 1}
            onClick={() => onPageClick(1)}
          >
            1
          </PagerButton>,
        )
        pages.push(<div key="ellipsis1">...</div>)
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          pages.push(
            <PagerButton
              key={i}
              page={i}
              className={`page-${i}`}
              active={currentPage === i}
              onClick={() => onPageClick(i)}
            >
              {i}
            </PagerButton>,
          )
        }
        pages.push(<div key="ellipsis2">...</div>)
        pages.push(
          <PagerButton
            key={numberOfPages}
            page={numberOfPages}
            className={`page-${numberOfPages}`}
            active={currentPage === numberOfPages}
            onClick={() => onPageClick(numberOfPages)}
          >
            {numberOfPages}
          </PagerButton>,
        )
      }
    }

    return pages
  }, [currentPage, numberOfPages, onPageClick])

  return (
    <Layout
      className="footer"
      css={{ paddingTop: 28, width: "100%" }}
      role="contentinfo"
    >
      {total ? (
        <Layout
          className="page-info"
          css={{
            justifyContent: "space-between",
            width: "100%",
            gap: 20,
            flexWrap: "wrap",
            fontSize: 12,
            color: colors.grey400,
          }}
        >
          {t("widgets.table.record_count", {
            start: (currentPage - 1) * perPage + 1,
            end: end <= total ? end : total,
            count: total,
          })}
          <div>
            {numberOfPages && total > perPage && (
              <Layout
                className="footer-buttons"
                css={{ gap: 34, flexWrap: "wrap" }}
              >
                <TextButton
                  disabled={currentPage === 1}
                  onClick={() => currentPage !== 1 && navigateToPage("prev")}
                  label="Previous"
                />
                <Layout
                  className="pager-buttons"
                  css={{ gap: 5, flexWrap: "wrap" }}
                >
                  {_.map(pagerButtons, (Pager) => Pager)}
                </Layout>
                <TextButton
                  disabled={currentPage === numberOfPages}
                  onClick={() =>
                    currentPage !== numberOfPages && navigateToPage("next")
                  }
                  label="Next"
                />
              </Layout>
            )}
          </div>
        </Layout>
      ) : null}
    </Layout>
  )
})

Footer.displayName = "Footer"

export const Header = memo(({ columns }) => (
  <thead className="table-header">
    <tr className="header-row" css={[rowStyle, { borderRadius: 7 }]}>
      {_.map(_.compact(columns), (column, index) =>
        _.isString(column) ? (
          <th key={index} className="header-cell" css={typography.tableHeader}>
            {column}
          </th>
        ) : (
          column
        ),
      )}
    </tr>
  </thead>
))

Header.displayName = "Header"

export const TableHeading = ({ className,children, columns = 3 }) => (
  <Layout
    className={classes(className,"table-heading")}
    css={[
      {
        justifyContent: "space-between",
        fontSize: "1rem",
        paddingTop: 35,
        paddingBottom: 35,
        whiteSpace: "break-spaces",
        width: "100%",
        display: "grid",
        gap: 20,
        gridTemplateColumns: `repeat(${columns},1fr)`,
        justifyItems: "center",
      },
      belowViewPort(1441, { fontSize: "0.75rem" }),
    ]}
  >
    {children}
  </Layout>
)

export const Table = forwardRef(
  (
    {
      title,
      perPage,
      total,
      hideFooter,
      tableStyle: propsTableStyle,
      Heading,
      tableContainerStyle,
      captionStyle,
      isPending,
      className,
      children,
      heading,
      onChangePage,
    },
    ref,
  ) => (
    <Layout
      className={classes("table-container-card", className)}
      css={[
        {
          padding: "35px 20px",
          borderRadius: 6,
          background: colors.cream,
        },
        Heading && { paddingTop: 0 },
      ]}
      vertical
    >
      {Heading}
      <Layout
        className="table-container"
        css={[
          {
            width: "100%",
            borderRadius: 6,
            overflow: "auto",
            border: `1px solid var(--resbird-table-border-color)`,
            transition: "opacity 1s",
            opacity: isPending ? 0.6 : 1,
            pointer: isPending ? "none" : "auto",
          },
          tableContainerStyleDefault,
          tableContainerStyle,
        ]}
        vertical
      >
        {heading}
        <table
          className={classes("table")}
          css={[tableStyle, propsTableStyle]}
          ref={ref}
        >
          {title && (
            <caption
              className="table-title"
              css={[{ marginBottom: 33 }, typography.widgetLabel, captionStyle]}
            >
              {title}
            </caption>
          )}
          {children}
        </table>
      </Layout>
      {!hideFooter && (
        <Footer perPage={perPage} total={total} onChangePage={onChangePage} />
      )}
    </Layout>
  ),
)

Table.displayName = "Table"
